import { ChevronDown, Filter, Pencil, Search, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { contractorService } from '@/services/contractor.service';
import type { Contractor, ContractorQueryParams, PaginatedResponse } from '@/types/contractor';
import { EntityStatus } from '@/types/contractor';

export function Contractors() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contractorsData, setContractorsData] = useState<PaginatedResponse<Contractor> | null>(
    null
  );

  const filterOptions = ['All Status', 'Active', 'Inactive'];

  // Get state from URL search params
  const searchQuery = searchParams.get('search') || '';
  const filterStatus = searchParams.get('status') || 'All Status';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Update URL search params
  const updateSearchParams = (updates: Record<string, string | number>) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'All Status' || value === 1) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  // Fetch contractors
  const fetchContractors = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: ContractorQueryParams = {
        page: currentPage,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (filterStatus === 'Active') {
        params.status = EntityStatus.ACTIVE;
      } else if (filterStatus === 'Inactive') {
        params.status = EntityStatus.INACTIVE;
      }

      const data = await contractorService.getAll(params);
      setContractorsData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contractors');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, filterStatus]);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  const handleEdit = (id: string) => {
    navigate(`/contractors/${id}/edit`);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this contractor?')) {
      return;
    }

    try {
      await contractorService.delete(id);
      fetchContractors(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contractor');
    }
  };

  // Reset to page 1 when filter changes
  const handleFilterChange = (newFilter: string) => {
    updateSearchParams({ status: newFilter, page: 1, search: searchQuery });
  };

  const handleSearchChange = (query: string) => {
    updateSearchParams({ search: query, page: 1, status: filterStatus });
  };

  const handlePageChange = (page: number) => {
    updateSearchParams({ page, search: searchQuery, status: filterStatus });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-[2px]">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={star <= rating ? 'fill-[#fbbf24] text-[#fbbf24]' : 'text-[#e5e5e5]'}
          />
        ))}
        <span className="ml-[4px] text-[12px] text-[#646464]">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const contractors = contractorsData?.data || [];
  const pagination = contractorsData?.pagination;

  return (
    <div className="space-y-[32px]">
      {/* Search and Filter Bar */}
      <div className="flex items-center gap-[12px]">
        {/* Search Input */}
        <div className="relative w-[60%]">
          <Search className="absolute left-[16px] top-1/2 h-5 w-5 -translate-y-1/2 text-[#8d8c8f]" />
          <input
            type="text"
            placeholder="Search contractors..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white pl-[48px] pr-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
          />
        </div>

        {/* Filter Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white pl-[16px] pr-[16px] text-[14px] font-medium text-[#181818] transition-all hover:border-[#646464] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
          >
            <Filter className="h-[18px] w-[18px] text-[#8d8c8f]" />
            <span className="whitespace-nowrap">{filterStatus}</span>
            <ChevronDown
              className={`h-[18px] w-[18px] text-[#8d8c8f] transition-transform ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute left-0 top-[52px] z-10 min-w-[180px] overflow-hidden rounded-[8px] border border-[#e5e5e5] bg-white shadow-lg">
              {filterOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    handleFilterChange(option);
                    setIsDropdownOpen(false);
                  }}
                  className={`flex w-full items-center gap-[8px] px-[16px] py-[12px] text-left text-[14px] transition-colors ${
                    filterStatus === option
                      ? 'bg-[#f5f5f5] font-semibold text-[#055135]'
                      : 'font-medium text-[#181818] hover:bg-[#fafafa]'
                  }`}
                >
                  {filterStatus === option && <span className="text-[#055135]">&#10003;</span>}
                  <span className={filterStatus === option ? '' : 'ml-[20px]'}>{option}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-[8px] border border-[#fca5a5] bg-[#fef2f2] p-[16px] text-[14px] text-[#b91c1c]">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-[64px]">
          <div className="h-[32px] w-[32px] animate-spin rounded-full border-2 border-[#055135] border-t-transparent" />
        </div>
      )}

      {/* Contractors Table */}
      {!isLoading && (
        <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#e5e5e5] bg-[#f5f5f5]">
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Contractor
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Company
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Rating
                </th>
                <th className="px-[24px] py-[16px] text-left text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Status
                </th>
                <th className="px-[24px] py-[16px] text-right text-[12px] font-semibold uppercase tracking-wide text-[#646464]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {contractors.map((contractor) => (
                <tr
                  key={contractor._id}
                  className="border-b border-[#e5e5e5] last:border-0 hover:bg-[#fafafa]"
                >
                  <td className="px-[24px] py-[20px]">
                    <div className="flex items-center gap-[12px]">
                      {contractor.profileLogo ? (
                        <img
                          src={contractor.profileLogo}
                          alt={contractor.name}
                          className="h-[40px] w-[40px] rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#f5f5f5] text-[14px] font-semibold text-[#646464]">
                          {contractor.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-[14px] font-semibold text-[#181818]">
                        {contractor.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-[24px] py-[20px] text-[14px] text-[#646464]">
                    {contractor.companyName}
                  </td>
                  <td className="px-[24px] py-[20px]">{renderStars(contractor.rating)}</td>
                  <td className="px-[24px] py-[20px]">
                    <span
                      className={`inline-flex items-center rounded-[6px] px-[12px] py-[4px] text-[12px] font-semibold ${
                        contractor.status === EntityStatus.ACTIVE
                          ? 'bg-[#d5eee9] text-[#055135]'
                          : 'bg-[#f5f5f5] text-[#646464]'
                      }`}
                    >
                      {contractor.status === EntityStatus.ACTIVE ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-[24px] py-[20px]">
                    <div className="flex items-center justify-end gap-[8px]">
                      <button
                        type="button"
                        onClick={() => handleEdit(contractor._id)}
                        className="flex h-[36px] w-[36px] items-center justify-center rounded-[6px] text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818]"
                        aria-label="Edit contractor"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(contractor._id)}
                        disabled={contractor.status === EntityStatus.INACTIVE}
                        className={`flex h-[36px] w-[36px] items-center justify-center rounded-[6px] transition-colors ${
                          contractor.status === EntityStatus.INACTIVE
                            ? 'cursor-not-allowed text-[#d0d0d0]'
                            : 'text-[#646464] hover:bg-[#fee] hover:text-[#d4183d]'
                        }`}
                        aria-label="Delete contractor"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {contractors.length === 0 && (
            <div className="py-[64px] text-center">
              <p className="text-[14px] text-[#8d8c8f]">No contractors found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-[4px] py-[12px]">
          <div className="text-[13px] text-[#8d8c8f]">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}{' '}
            results
          </div>
          <div className="flex items-center gap-[6px]">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="flex h-[32px] items-center gap-[4px] rounded-[6px] px-[12px] text-[13px] font-medium text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#646464]"
            >
              Previous
            </button>
            <div className="flex items-center gap-[2px]">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={`flex h-[32px] w-[32px] items-center justify-center rounded-[6px] text-[13px] font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-[#055135] text-white'
                      : 'text-[#646464] hover:bg-[#f5f5f5] hover:text-[#181818]'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNext}
              className="flex h-[32px] items-center gap-[4px] rounded-[6px] px-[12px] text-[13px] font-medium text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#646464]"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
